/* eslint-disable no-undef */

import { escapeODataLiteral, buildODataQuery, formatODataHttpError, parseODataErrorCode, BcHttpError } from "../shared/odata";
import type { BcRequestContext } from "../shared/auth-state";

export interface BcOperationalOrder {
  id?: string;
  orderNo: string;
  customerName?: string;
  internalReference?: string;
  externalReference?: string;
  bookingReference?: string;
  mblNo?: string;
}


export interface DocType {
    code: string;
    description: string;
    // Sort key for the Document Type dropdown -- see fetchDocumentType/loadDocTypes.
    emailBridgeSequence: number;
    // Bolded in the Document Type dropdown when true -- see renderFileRow in attachments.ts.
    emailBridgeRequired: boolean;
}

interface BcListResponse<T> {
  value?: T[];
}

export interface BcFetchOptions {
  signal?: AbortSignal;
}

export interface LinkOrderDocumentBody {
  orderNo: string;
  orderLineNo: number;
  fileName: string;
  documentType: string;
  comments: string;
  allowSynchToPortal: boolean;
  uploadedBy: string;
  sourceType?: string;
  fileContent: string;
}

export interface LinkOrderDocumentResult {
  body: LinkOrderDocumentBody;
  // Generated here and sent as x-ms-client-request-id -- lets a specific attempt be
  // pointed out precisely (e.g. to BC/AL support searching their own telemetry) even on a
  // success, independent of whatever BC itself does or doesn't echo back.
  clientRequestId: string;
  // BC's own request/correlation id read back from the response headers, if it sent one
  // under a header name we recognize (see readBcResponseRequestId) -- null otherwise.
  bcRequestId: string | null;
}

// crypto.randomUUID is available in every WebView2/Edge runtime this add-in targets; the
// fallback only guards against a runtime where it's somehow missing, not IE11 support.
function generateClientRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// Candidate response header names for BC's own request/correlation id -- tried in order,
// first match wins. Not verified against a real BC response yet (no production traffic
// capture to confirm which one, if any, this environment's BC actually sends); harmless
// to keep trying all three since an absent header just reads back null.
const BC_RESPONSE_REQUEST_ID_HEADERS = ["request-id", "x-ms-request-id", "client-request-id"];

function readBcResponseRequestId(resp: Response): string | null {
  for (const headerName of BC_RESPONSE_REQUEST_ID_HEADERS) {
    const value = resp.headers.get(headerName);
    if (value) return value;
  }
  return null;
}

export async function linkOrderDocument(
  orderNo: string,
  fileName: string,
  fileContent: Uint8Array,
  documentType: string,
  comments: string,
  allowSynchToPortal: boolean,
  uploadedBy: string,
  bc: BcRequestContext,
): Promise<LinkOrderDocumentResult> {
  const url = `${bc.bcUrl}/api/logiqstar/ffw/v2.0/companies(${bc.companyId})/ord_linkedDocuments_v2`;
  let binary = "";
  const chunkSize = 8192;
  for (let i = 0; i < fileContent.length; i += chunkSize) {
    binary += String.fromCharCode(...fileContent.subarray(i, i + chunkSize));
  }
  const base64 = btoa(binary);
  const body: LinkOrderDocumentBody = {
    orderNo,
    orderLineNo: 10000,
    fileName,
    documentType,
    comments,
    allowSynchToPortal,
    uploadedBy,
    sourceType: "External",
    fileContent: base64,
  };
  const clientRequestId = generateClientRequestId();
  const resp = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${bc.token}`,
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-ms-client-request-id": clientRequestId,
    },
    body: JSON.stringify(body),
  });
  const bcRequestId = readBcResponseRequestId(resp);
  if (!resp.ok) {
    const raw = await resp.text();
    const msg = formatODataHttpError(resp.status, raw);
    throw new BcHttpError(resp.status, msg, { code: parseODataErrorCode(raw), clientRequestId, bcRequestId });
  }
  return { body, clientRequestId, bcRequestId };
}

/**
 * Scopes filterExpr to the signed-in user's department (bc.departmentSetEntryIdFlt, from
 * user_details -- see loadAndStoreDepartmentSetEntryId in companies.ts), wrapping the
 * existing expression in parens first so this "and" doesn't silently bind tighter than an
 * "or" already inside it (OData, like most languages, evaluates "and" before "or"). Returns
 * null if the department value hasn't loaded yet -- callers treat that as "return zero
 * results" rather than searching unfiltered, since every match this API can return is only
 * meaningful once scoped to the user's department.
 */
function withDepartmentFilter(filterExpr: string, bc: BcRequestContext): string | null {
  const dept = bc.departmentSetEntryIdFlt.trim();
  if (!dept) return null;
  return `(${filterExpr}) and departmentSetEntryIDFilter eq '${escapeODataLiteral(dept)}'`;
}

const EMAIL_BRIDGE_ORDERS_SELECT = "id,orderNo,customerName,internalReference,externalReference,bookingReference,mblNo";

/**
 * Queries email_bridge_orders with a department-scoped $filter expression (see
 * withDepartmentFilter) and the $select shared by every lookup against this entity. Field
 * names here (orderNo, internalReference, ...) already match BcOperationalOrder directly --
 * unlike operationalOrders, no raw-to-mapped translation is needed. Returns [] without a
 * request if the department value hasn't loaded yet.
 */
async function fetchEmailBridgeOrders(
  filterExpr: string,
  bc: BcRequestContext,
  options?: BcFetchOptions,
  orderBy?: string,
): Promise<BcOperationalOrder[]> {
  const filter = withDepartmentFilter(filterExpr, bc);
  if (filter === null) return [];
  const query = buildODataQuery({
    $select: EMAIL_BRIDGE_ORDERS_SELECT,
    $filter: filter,
    $orderby: orderBy,
  });
  const url = `${bc.bcUrl}/api/logiqstar/email/v2.0/companies(${bc.companyId})/email_bridge_orders${query}`;
  const resp = await fetch(url, {
    headers: { Authorization: `Bearer ${bc.token}`, Accept: "application/json" },
    signal: options?.signal,
  });
  if (!resp.ok) {
    const raw = await resp.text();
    const msg = formatODataHttpError(resp.status, raw);
    throw new BcHttpError(resp.status, msg, { code: parseODataErrorCode(raw) });
  }
  const data = (await resp.json()) as BcListResponse<BcOperationalOrder>;
  return data.value ?? [];
}

export async function fetchOperationalOrdersByNo(
  searchfilter: string,
  bc: BcRequestContext,
  options?: BcFetchOptions,
): Promise<BcOperationalOrder[]> {
  return fetchEmailBridgeOrders(`searchFilter eq '${searchfilter}'`, bc, options, "createdAt desc");
}

/**
 * Looks up the shared "ordersURL" link for a set of orders (all orderNo's OData-literal-
 * escaped by the caller), used to make the post-upload status banner clickable -- see
 * findOrdersUrlForOrderNos in bc-service.ts. ordersURL is the same across every matching
 * record, so only the first one is read. Not department-filtered: orderNos here are always
 * orders that were already found (and thus already department-scoped) via
 * fetchOperationalOrdersByNo, so re-applying the filter here would be redundant.
 */
export async function fetchOrdersUrlByOrderNos(
  orderNos: string[],
  bc: BcRequestContext,
  options?: BcFetchOptions,
): Promise<string | null> {
  if (orderNos.length === 0) return null;
  const filter = orderNos.map((no) => `orderNo eq '${no}'`).join(" or ");
  const query = buildODataQuery({ $select: "ordersURL", $filter: filter, $top: "1" });
  const url = `${bc.bcUrl}/api/logiqstar/email/v2.0/companies(${bc.companyId})/email_bridge_orders${query}`;
  const resp = await fetch(url, {
    headers: { Authorization: `Bearer ${bc.token}`, Accept: "application/json" },
    signal: options?.signal,
  });
  if (!resp.ok) {
    const raw = await resp.text();
    const msg = formatODataHttpError(resp.status, raw);
    throw new BcHttpError(resp.status, msg, { code: parseODataErrorCode(raw) });
  }
  const data = (await resp.json()) as BcListResponse<{ ordersURL?: string }>;
  return data.value?.[0]?.ordersURL ?? null;
}

/**
 * Checks whether a document with this exact fileName -- optionally scoped to a single
 * orderNo -- is already linked in ord_linkedDocuments_v2. Purely informational: used for
 * the "uploaded" badge on the attachments list (see refreshUploadedBadges in
 * attachments.ts), a live replacement for the old session-memory tracking that didn't
 * survive a page reload. fileName/orderNo must already be OData-literal-escaped by the
 * caller.
 */
export async function checkLinkedDocumentExists(
  fileName: string,
  orderNo: string | null,
  bc: BcRequestContext,
  options?: BcFetchOptions,
): Promise<boolean> {
  let filter = `fileName eq '${fileName}'`;
  if (orderNo) filter += ` and orderNo eq '${orderNo}'`;
  const query = buildODataQuery({ $filter: filter, $top: "1" });
  const url = `${bc.bcUrl}/api/logiqstar/ffw/v2.0/companies(${bc.companyId})/ord_linkedDocuments_v2${query}`;
  const resp = await fetch(url, {
    headers: { Authorization: `Bearer ${bc.token}`, Accept: "application/json" },
    signal: options?.signal,
  });
  if (!resp.ok) {
    const raw = await resp.text();
    const msg = formatODataHttpError(resp.status, raw);
    throw new BcHttpError(resp.status, msg, { code: parseODataErrorCode(raw) });
  }
  const data = (await resp.json()) as BcListResponse<unknown>;
  return (data.value ?? []).length > 0;
}

/**
 * Looks up operational orders by exact order number, used for auto-selecting an order
 * from an email subject (see findOperationalOrderByNo in bc-service.ts). Queries
 * email_bridge_orders (not operationalOrders) so the same department scoping already
 * applied to manual search also covers auto-select. orderNo must already be
 * OData-literal-escaped by the caller.
 */
export async function fetchOperationalOrderByNo(
  orderNo: string,
  bc: BcRequestContext,
  options?: BcFetchOptions,
): Promise<BcOperationalOrder[]> {
  return fetchEmailBridgeOrders(`orderNo eq '${orderNo}'`, bc, options);
}

/**
 * Looks up operational orders by exact internalReference, used as the auto-select
 * fallback when no order-number-shaped word matches (see
 * findOperationalOrderByInternalReference in bc-service.ts). Queries email_bridge_orders,
 * same as fetchOperationalOrderByNo. internalReference must already be
 * OData-literal-escaped by the caller.
 */
export async function fetchOperationalOrderByInternalReference(
  internalReference: string,
  bc: BcRequestContext,
  options?: BcFetchOptions,
): Promise<BcOperationalOrder[]> {
  return fetchEmailBridgeOrders(`internalReference eq '${internalReference}'`, bc, options);
}

export async function fetchDocumentType(
  bc: BcRequestContext,
  options?: BcFetchOptions
): Promise<DocType[]> {
    const query = buildODataQuery({
      $filter: "emailBridgeApplicable eq true",
      $orderby: "emailBridgeSequence,code",
    });
    const url = `${bc.bcUrl}/api/logiqstar/ffw/v2.0/companies(${bc.companyId})/document_types${query}`;
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${bc.token}`, Accept: "application/json" },
        signal: options?.signal,
      });
      if (!resp.ok) {
        const raw = await resp.text();
        const msg = formatODataHttpError(resp.status, raw);
        throw new BcHttpError(resp.status, msg, { code: parseODataErrorCode(raw) });
      }
      const data = (await resp.json()) as BcListResponse<DocType>;
      return data.value ?? [];
}
